import React, { useState } from 'react';
import {
  FormControl, Select, MenuItem, SelectChangeEvent,
  Box, Typography, ToggleButtonGroup, ToggleButton, Tooltip,
  IconButton, Popover,
} from '@mui/material';
import { WbSunny as SunIcon, DarkMode as MoonIcon, Palette as PaletteIcon } from '@mui/icons-material';
import { useTheme, Theme, themeDefinitions } from './ThemeProvider';

interface ThemeSwitcherProps {
  className?: string;
  compact?: boolean;
  toolbar?: boolean;
}

const ThemeSwitcher: React.FC<ThemeSwitcherProps> = ({ className = '', compact = false, toolbar = false }) => {
  const { theme, mode, setTheme, setMode, toggleTheme, availableThemes } = useTheme();
  const [paletteAnchor, setPaletteAnchor] = useState<null | HTMLElement>(null);

  const getThemeIcon = (themeName: Theme) => {
    switch (themeName) {
      case 'blue':    return '🔵';
      case 'green':   return '🟢';
      case 'purple':  return '🟣';
      case 'orange':  return '🟠';
      case 'teal':    return '🔵';
      case 'windows': return '🪟';
      case 'mac':     return '🍎';
      case 'linux':   return '🐧';
      case 'office':  return '🏢';
      default:        return '🎨';
    }
  };

  const getThemeName = (themeName: Theme) =>
    themeName.charAt(0).toUpperCase() + themeName.slice(1);

  const handleThemeChange = (event: SelectChangeEvent<string>) => {
    setTheme(event.target.value as Theme);
  };

  const handleModeChange = (event: SelectChangeEvent<string>) => {
    setMode(event.target.value as 'light' | 'dark');
  };

  const swatches = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {availableThemes.map(t => (
        <Tooltip key={t} title={`${getThemeName(t)}${theme === t ? ' (Active)' : ''}`} placement="top">
          <Box
            onClick={() => {
              setTheme(t);
              setPaletteAnchor(null);
            }}
            sx={{
              width: 28, height: 28, borderRadius: '50%',
              bgcolor: themeDefinitions[t].light.primary,
              cursor: 'pointer',
              border: theme === t ? '3px solid' : '2px solid transparent',
              borderColor: theme === t ? 'text.primary' : 'transparent',
              outline: theme === t ? '2px solid' : 'none',
              outlineColor: theme === t ? themeDefinitions[t].light.primary : 'transparent',
              outlineOffset: '2px',
              transition: 'transform 0.15s',
              '&:hover': { transform: 'scale(1.2)' },
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );

  /* ── Toolbar variant (desktop AppBar) ─────────────────────── */
  if (toolbar) {
    return (
      <Box className={className} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Light / Dark toggle */}
        <Tooltip title={mode === 'light' ? 'Switch to Dark mode' : 'Switch to Light mode'}>
          <IconButton color="inherit" onClick={toggleTheme} size="small">
            {mode === 'light' ? <MoonIcon fontSize="small" /> : <SunIcon fontSize="small" />}
          </IconButton>
        </Tooltip>

        {/* Palette popover */}
        <Tooltip title="Theme colour">
          <IconButton
            color="inherit"
            size="small"
            onClick={e => setPaletteAnchor(e.currentTarget)}
          >
            <PaletteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Popover
          open={Boolean(paletteAnchor)}
          anchorEl={paletteAnchor}
          onClose={() => setPaletteAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { p: 1.5, mt: 0.5 } } }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, color: 'text.secondary' }}>
            Theme
          </Typography>
          {swatches}
        </Popover>
      </Box>
    );
  }

  /* ── Compact variant (mobile profile menu) ────────────────── */
  if (compact) {
    return (
      <Box className={className}>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 600, color: 'text.secondary' }}>
          Mode
        </Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, val) => val && setMode(val as 'light' | 'dark')}
          size="small"
          fullWidth
          sx={{ mb: 1.25 }}
        >
          <ToggleButton value="light" sx={{ flex: 1, gap: 0.5, fontSize: 13 }}>
            <span>☀️</span> Light
          </ToggleButton>
          <ToggleButton value="dark" sx={{ flex: 1, gap: 0.5, fontSize: 13 }}>
            <span>🌙</span> Dark
          </ToggleButton>
        </ToggleButtonGroup>

        <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, color: 'text.secondary' }}>
          Theme
        </Typography>
        {swatches}
      </Box>
    );
  }

  /* ── Default dropdown variant ─────────────────────────────── */
  return (
    <Box className={className} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <Box sx={{ flex: '1 1 90px' }}>
        <Typography variant="caption" sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
          Mode
        </Typography>
        <FormControl size="small" fullWidth>
          <Select value={mode} onChange={handleModeChange}
            sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 } }}
          >
            <MenuItem value="light">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><span>☀️</span><span>Light</span></Box>
            </MenuItem>
            <MenuItem value="dark">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><span>🌙</span><span>Dark</span></Box>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ flex: '1 1 110px' }}>
        <Typography variant="caption" sx={{ mb: 0.5, display: 'block', color: 'text.secondary' }}>
          Theme
        </Typography>
        <FormControl size="small" fullWidth>
          <Select value={theme} onChange={handleThemeChange} displayEmpty
            sx={{ '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 } }}
          >
            {availableThemes.map(themeName => (
              <MenuItem key={themeName} value={themeName}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{getThemeIcon(themeName)}</span>
                  <span>{getThemeName(themeName)}</span>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default ThemeSwitcher;
